import { useCallback, useMemo, useState } from 'react';
import { useForm, type DefaultValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useUI } from '@/hooks/ui/useUI';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { useTranslation } from '@/i18n';
import { shiftService } from '@/api/domains/shift';
import type { ScheduleOption } from '@/api/domains/shift';
import type { SelectorOption } from '@/components/ui/inputs/selector/Selector.types';
import {
  assignScheduleSchema,
  type AssignScheduleForm,
  type AssignScheduleFormSubset,
} from '@/schemas/shift/assignScheduleForm';


const SCHEDULE_CATALOG_PAGE_SIZE = 1000;

export interface AssignScheduleReplicableDay {
  daySequence: number;
  label: string;
}

export interface AssignScheduleExistingAssignment {
  schedule1Id: number | null;
  schedule2Id: number | null;
}

export interface AssignScheduleResult {
  schedule1Id: number;
  schedule2Id: number | null;
  replicateDaySequences: number[];
}

interface UseAssignScheduleFormPanelParams {
  replicableDays: AssignScheduleReplicableDay[];
  existingAssignment?: AssignScheduleExistingAssignment;
  onAssign: (result: AssignScheduleResult) => void;
  onClear?: () => void;
}

export interface UseAssignScheduleFormPanelResult {
  formSubset: AssignScheduleFormSubset;
  scheduleOptions: SelectorOption[];
  isLoadingSchedules: boolean;
  hasSecondSchedule: boolean;
  schedule1Details: ScheduleOption | null;
  schedule2Details: ScheduleOption | null;
  replicableDays: AssignScheduleReplicableDay[];
  selectedReplicateDays: number[];
  isReady: boolean;
  isEditMode: boolean;
  addSecondSchedule: () => void;
  removeSecondSchedule: () => void;
  toggleReplicateDay: (daySequence: number) => void;
  handleSubmit: () => void;
  handleClear: () => void;
}

const DEFAULT_VALUES: DefaultValues<AssignScheduleForm> = {
  schedule1Id: null,
  schedule2Id: null,
  replicateDaySequences: [],
};

export function useAssignScheduleFormPanel({
  replicableDays,
  existingAssignment,
  onAssign,
  onClear,
}: UseAssignScheduleFormPanelParams): UseAssignScheduleFormPanelResult {
  const { t: translate } = useTranslation('schedule');
  const { showNotification, closeSidePanel } = useUI();

  const {
    register,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<AssignScheduleForm>({
    resolver: zodResolver(assignScheduleSchema),
    mode: 'onChange',
    defaultValues: existingAssignment
      ? {
        schedule1Id: existingAssignment.schedule1Id,
        schedule2Id: existingAssignment.schedule2Id,
        replicateDaySequences: [],
      }
      : DEFAULT_VALUES,
  });

  const formSubset: AssignScheduleFormSubset = {
    register, setValue, watch, errors, trigger,
  };

  const { data: schedules, options: scheduleOptions, isLoaded } = useCatalogLoader(
    async (signal) => {
      const response = await shiftService.getSchedules(
        { pageSize: SCHEDULE_CATALOG_PAGE_SIZE },
        signal,
      );
      if (!response.success || !response.data) return { ...response, data: null };
      return { ...response, data: response.data.items };
    },
    (item: ScheduleOption) => ({
      value: String(item.id),
      label: `${item.name} (${item.startTime} - ${item.endTime})`,
    }),
    [],
    false,
    () => showNotification('error', translate('shift.create.assignSchedule.messages.loadSchedulesError')),
  );

  const [isSecondScheduleVisible, setIsSecondScheduleVisible] = useState<boolean>(
    existingAssignment?.schedule2Id != null,
  );

  const schedule1Id = watch('schedule1Id');
  const schedule2Id = watch('schedule2Id');
  const watchedReplicateDaySequences = watch('replicateDaySequences');
  const replicateDaySequences = useMemo(
    () => watchedReplicateDaySequences ?? [],
    [watchedReplicateDaySequences],
  );

  const findSchedule = useCallback(
    (id: number | null): ScheduleOption | null => {
      if (id === null) return null;
      return schedules.find((schedule) => schedule.id === id) ?? null;
    },
    [schedules],
  );

  const schedule1Details = useMemo(() => findSchedule(schedule1Id), [findSchedule, schedule1Id]);
  const schedule2Details = useMemo(() => findSchedule(schedule2Id), [findSchedule, schedule2Id]);

  const addSecondSchedule = useCallback(() => {
    setIsSecondScheduleVisible(true);
  }, []);

  const removeSecondSchedule = useCallback(() => {
    setIsSecondScheduleVisible(false);
    setValue('schedule2Id', null, { shouldDirty: true });
  }, [setValue]);

  const toggleReplicateDay = useCallback(
    (daySequence: number) => {
      const isSelected = replicateDaySequences.includes(daySequence);
      const next = isSelected
        ? replicateDaySequences.filter((sequence) => sequence !== daySequence)
        : [...replicateDaySequences, daySequence];
      setValue('replicateDaySequences', next, { shouldDirty: true });
    },
    [replicateDaySequences, setValue],
  );

  const isReady = schedule1Id !== null;

  const handleSubmit = useCallback(() => {
    if (schedule1Id === null) {
      void trigger('schedule1Id');
      return;
    }

    onAssign({
      schedule1Id,
      schedule2Id,
      replicateDaySequences,
    });
    closeSidePanel();
  }, [schedule1Id, schedule2Id, replicateDaySequences, onAssign, closeSidePanel, trigger]);

  const isEditMode = existingAssignment != null;

  const handleClear = useCallback(() => {
    onClear?.();
    closeSidePanel();
  }, [onClear, closeSidePanel]);

  return {
    formSubset,
    scheduleOptions,
    isLoadingSchedules: !isLoaded,
    hasSecondSchedule: isSecondScheduleVisible,
    schedule1Details,
    schedule2Details,
    replicableDays,
    selectedReplicateDays: replicateDaySequences,
    isReady,
    isEditMode,
    addSecondSchedule,
    removeSecondSchedule,
    toggleReplicateDay,
    handleSubmit,
    handleClear,
  };
}
